import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Lock,
  CreditCard,
  ShieldCheck,
  Check,
  User,
  MapPin,
  Calendar,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { BRAND_INFO, CONTACT_INFO } from '../data/mock';
import { useCart } from '../context/CartContext';

const PROCESSING_FEE = 5;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const LAB_CHOICES = [
  { id: 'labcorp',     name: 'LabCorp',          desc: '~2,000 draw locations nationwide' },
  { id: 'quest',       name: 'Quest Diagnostics', desc: '~2,200 draw locations nationwide' },
  { id: 'bioreference', name: 'BioReference',     desc: 'Available in select metros' },
];

const initialForm = {
  firstName: '',
  lastName: '',
  dob: '',
  sex: 'male',
  email: '',
  phone: '',
  street: '',
  street2: '',
  city: '',
  state: 'IL',
  zip: '',
  lab: 'labcorp',
  cardName: '',
  cardNumber: '',
  exp: '',
  cvc: '',
  agree: false,
};

const Step = ({ n, label, current }) => (
  <div className="flex items-center gap-2">
    <span
      className={`w-7 h-7 inline-flex items-center justify-center rounded-full text-[12px] font-semibold ${
        current >= n ? 'bg-brick-500 text-white' : 'bg-cream-200 text-ink-600'
      }`}
    >
      {current > n ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : n}
    </span>
    <span
      className={`text-[12px] uppercase tracking-[0.18em] ${
        current >= n ? 'text-ink-900' : 'text-ink-500'
      }`}
    >
      {label}
    </span>
  </div>
);

const Field = ({ label, children, error, required }) => (
  <label className="block">
    <span className="text-[12px] uppercase tracking-[0.16em] text-ink-500">
      {label}
      {required && <span className="ml-1 text-brick-500">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
    {error ? <span className="mt-1 text-[12px] text-brick-600">{error}</span> : null}
  </label>
);

const input =
  'w-full px-4 py-3 rounded-xl bg-white border border-cream-300 text-ink-900 text-[14px] focus:border-brick-400';

const CheckoutPage = () => {
  usePageTitle(`Checkout | ${BRAND_INFO.name}`);
  const navigate = useNavigate();
  const { items, subtotal, totalQty } = useCart();

  const [step, setStep] = useState(1); // 1 patient, 2 lab, 3 payment, 4 review
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Redirect away if cart empty
  useEffect(() => {
    if (totalQty === 0) {
      const t = setTimeout(() => navigate('/cart', { replace: true }), 50);
      return () => clearTimeout(t);
    }
  }, [totalQty, navigate]);

  const fee = items.length > 0 ? PROCESSING_FEE : 0;
  const tax = useMemo(() => +(subtotal * 0).toFixed(2), [subtotal]); // health labs untaxed in most states
  const total = subtotal + fee + tax;

  // ---- Validation ----
  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!form.firstName.trim()) errs.firstName = 'Required';
      if (!form.lastName.trim()) errs.lastName = 'Required';
      if (!form.dob) errs.dob = 'Required';
      if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Valid email required';
      if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10) errs.phone = '10-digit phone required';
    }
    if (s === 2) {
      if (!form.street.trim()) errs.street = 'Required';
      if (!form.city.trim()) errs.city = 'Required';
      if (!form.zip.trim() || !/^\d{5}(-\d{4})?$/.test(form.zip)) errs.zip = '5-digit ZIP';
    }
    if (s === 3) {
      if (!form.cardName.trim()) errs.cardName = 'Required';
      if (!form.cardNumber.trim() || form.cardNumber.replace(/\s/g, '').length < 13)
        errs.cardNumber = 'Card number';
      if (!/^\d{2}\/\d{2}$/.test(form.exp)) errs.exp = 'MM/YY';
      if (!/^\d{3,4}$/.test(form.cvc)) errs.cvc = 'CVC';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const placeOrder = () => {
    if (!form.agree) {
      setErrors({ agree: 'Please agree to continue' });
      return;
    }
    setSubmitting(true);
    // Generate mock order id
    const id = `AS-${Date.now().toString(36).toUpperCase()}`;
    // Stash a snapshot for the confirmation page
    try {
      sessionStorage.setItem(
        `as_order_${id}`,
        JSON.stringify({
          id,
          createdAt: new Date().toISOString(),
          form,
          items,
          subtotal,
          fee,
          tax,
          total,
        })
      );
    } catch (e) {
      /* ignore */
    }
    setTimeout(() => {
      navigate(`/order/${id}`);
    }, 700);
  };

  if (totalQty === 0) {
    return null;
  }

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-8 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">Checkout</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900">
            One last thing.
          </h1>
          <p className="mt-3 text-ink-600 max-w-2xl">
            We need your name and date of birth so the lab can legally identify you on
            the requisition. Everything else is shipping-and-billing housekeeping.
          </p>

          {/* Step indicator */}
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Step n={1} label="Patient" current={step} />
            <span className="hidden sm:inline-block w-8 h-px bg-cream-300"></span>
            <Step n={2} label="Address & Lab" current={step} />
            <span className="hidden sm:inline-block w-8 h-px bg-cream-300"></span>
            <Step n={3} label="Payment" current={step} />
            <span className="hidden sm:inline-block w-8 h-px bg-cream-300"></span>
            <Step n={4} label="Review" current={step} />
          </div>
        </div>
      </section>

      <section className="pb-24 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-7 lg:p-9">
              {/* STEP 1 */}
              {step === 1 && (
                <div className="space-y-6">
                  <header className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-cream-100 text-brick-500 border border-cream-200">
                      <User className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="font-display text-2xl text-ink-900">Patient information</h2>
                      <p className="text-[13px] text-ink-500">
                        Required by the lab for requisition matching.
                      </p>
                    </div>
                  </header>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="First name" required error={errors.firstName}>
                      <input className={input} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                    </Field>
                    <Field label="Last name" required error={errors.lastName}>
                      <input className={input} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
                    </Field>
                    <Field label="Date of birth" required error={errors.dob}>
                      <input type="date" className={input} value={form.dob} onChange={(e) => set('dob', e.target.value)} />
                    </Field>
                    <Field label="Biological sex (for reference ranges)" required>
                      <select className={input} value={form.sex} onChange={(e) => set('sex', e.target.value)}>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="intersex">Prefer not to say</option>
                      </select>
                    </Field>
                    <Field label="Email" required error={errors.email}>
                      <input type="email" className={input} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
                    </Field>
                    <Field label="Phone" required error={errors.phone}>
                      <input className={input} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 555-5555" />
                    </Field>
                  </div>
                  <div className="flex justify-end">
                    <button onClick={next} className="btn-brick">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div className="space-y-6">
                  <header className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-cream-100 text-brick-500 border border-cream-200">
                      <MapPin className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="font-display text-2xl text-ink-900">Address & preferred lab</h2>
                      <p className="text-[13px] text-ink-500">
                        We use your ZIP to confirm a partner draw site near you.
                      </p>
                    </div>
                  </header>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Field label="Street address" required error={errors.street}>
                        <input className={input} value={form.street} onChange={(e) => set('street', e.target.value)} />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Suite, apt (optional)">
                        <input className={input} value={form.street2} onChange={(e) => set('street2', e.target.value)} />
                      </Field>
                    </div>
                    <Field label="City" required error={errors.city}>
                      <input className={input} value={form.city} onChange={(e) => set('city', e.target.value)} />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="State" required>
                        <select className={input} value={form.state} onChange={(e) => set('state', e.target.value)}>
                          {US_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="ZIP" required error={errors.zip}>
                        <input className={input} value={form.zip} onChange={(e) => set('zip', e.target.value)} placeholder="60067" />
                      </Field>
                    </div>
                  </div>

                  <div>
                    <div className="text-[12px] uppercase tracking-[0.16em] text-ink-500 mb-2">
                      Preferred lab network
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {LAB_CHOICES.map((lab) => {
                        const active = form.lab === lab.id;
                        return (
                          <button
                            type="button"
                            key={lab.id}
                            onClick={() => set('lab', lab.id)}
                            className={`text-left p-4 rounded-2xl border transition-all ${
                              active
                                ? 'bg-sage-50 border-sage-400 ring-1 ring-sage-300'
                                : 'bg-white border-cream-300 hover:border-ink-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-display text-lg text-ink-900">{lab.name}</div>
                              <span className={`w-4 h-4 rounded-full border ${active ? 'bg-sage-500 border-sage-500' : 'border-cream-400'}`}></span>
                            </div>
                            <div className="mt-1 text-[12px] text-ink-500">{lab.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button onClick={back} className="btn-ghost">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button onClick={next} className="btn-brick">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <div className="space-y-6">
                  <header className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-cream-100 text-brick-500 border border-cream-200">
                      <CreditCard className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="font-display text-2xl text-ink-900">Payment</h2>
                      <p className="text-[13px] text-ink-500">
                        Secured · charges appear under {BRAND_INFO.billingDescriptor}
                      </p>
                    </div>
                  </header>

                  <Field label="Name on card" required error={errors.cardName}>
                    <input className={input} value={form.cardName} onChange={(e) => set('cardName', e.target.value)} />
                  </Field>
                  <Field label="Card number" required error={errors.cardNumber}>
                    <div className="relative">
                      <input
                        className={input + ' pr-12'}
                        value={form.cardNumber}
                        onChange={(e) => {
                          // format with spaces every 4
                          const v = e.target.value.replace(/\D/g, '').slice(0, 19);
                          const grouped = v.match(/.{1,4}/g)?.join(' ') || '';
                          set('cardNumber', grouped);
                        }}
                        placeholder="4242 4242 4242 4242"
                        autoComplete="cc-number"
                      />
                      <Lock className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-ink-400" />
                    </div>
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Expiry (MM/YY)" required error={errors.exp}>
                      <input
                        className={input}
                        value={form.exp}
                        onChange={(e) => {
                          let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                          if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                          set('exp', v);
                        }}
                        placeholder="12/28"
                        autoComplete="cc-exp"
                      />
                    </Field>
                    <Field label="CVC" required error={errors.cvc}>
                      <input
                        className={input}
                        value={form.cvc}
                        onChange={(e) => set('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="123"
                        autoComplete="cc-csc"
                      />
                    </Field>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-cream-100 border border-cream-200 text-[12px] text-ink-700">
                    <ShieldCheck className="w-4 h-4 text-sage-700 mt-0.5" />
                    <span>
                      No payment is processed in this preview. Card fields are accepted as plain
                      text and never sent to a real processor. Wire up a payment processor when ready.
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <button onClick={back} className="btn-ghost">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button onClick={next} className="btn-brick">
                      Review order <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4 */}
              {step === 4 && (
                <div className="space-y-6">
                  <header className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-cream-100 text-brick-500 border border-cream-200">
                      <Calendar className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="font-display text-2xl text-ink-900">Review &amp; place order</h2>
                      <p className="text-[13px] text-ink-500">
                        One quick look before we generate your requisition.
                      </p>
                    </div>
                  </header>

                  <ReviewBlock title="Patient" onEdit={() => setStep(1)}>
                    <div>{form.firstName} {form.lastName}</div>
                    <div className="text-ink-500 text-[13px]">DOB {form.dob || '—'} · {form.sex}</div>
                    <div className="text-ink-500 text-[13px]">{form.email} · {form.phone}</div>
                  </ReviewBlock>
                  <ReviewBlock title="Address & lab" onEdit={() => setStep(2)}>
                    <div>
                      {form.street}{form.street2 ? `, ${form.street2}` : ''}
                    </div>
                    <div className="text-ink-500 text-[13px]">
                      {form.city}, {form.state} {form.zip}
                    </div>
                    <div className="text-ink-500 text-[13px] capitalize">
                      Preferred lab: {LAB_CHOICES.find((l) => l.id === form.lab)?.name}
                    </div>
                  </ReviewBlock>
                  <ReviewBlock title="Payment" onEdit={() => setStep(3)}>
                    <div>{form.cardName}</div>
                    <div className="text-ink-500 text-[13px]">
                      Card ending {form.cardNumber.replace(/\s/g, '').slice(-4) || '----'} · exp {form.exp || '--/--'}
                    </div>
                  </ReviewBlock>

                  <label className="flex items-start gap-3 text-[14px] text-ink-700">
                    <input
                      type="checkbox"
                      checked={form.agree}
                      onChange={(e) => set('agree', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-brick-500"
                    />
                    <span>
                      I confirm the patient information is accurate, I am 18+, and I agree to the{' '}
                      <Link to="/terms" className="underline text-brick-500">Terms</Link>,{' '}
                      <Link to="/privacy" className="underline text-brick-500">Privacy Policy</Link>, and{' '}
                      <Link to="/medical-disclaimer" className="underline text-brick-500">Medical Disclaimer</Link>.
                    </span>
                  </label>
                  {errors.agree && <p className="text-[12px] text-brick-600">{errors.agree}</p>}

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                    <button onClick={back} className="btn-ghost">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                      onClick={placeOrder}
                      disabled={submitting}
                      className="inline-flex items-center gap-2 bg-brick-500 hover:bg-brick-600 disabled:opacity-60 text-white font-medium px-7 py-3.5 rounded-full transition-colors"
                    >
                      {submitting ? 'Placing order…' : <>Place order · ${total.toFixed(2)} <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order summary side */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl text-ink-900">Order summary</h3>
                <Link to="/cart" className="text-[12px] text-ink-500 hover:text-brick-500 underline">
                  Edit
                </Link>
              </div>

              <ul className="mt-5 divide-y divide-cream-200">
                {items.map((it) => (
                  <li key={it.id} className="py-3 flex items-start justify-between gap-3 text-[14px]">
                    <div className="min-w-0">
                      <div className="text-ink-900 truncate">{it.name}</div>
                      <div className="text-[12px] text-ink-500">
                        {it.qty > 1 ? `Qty ${it.qty} · ` : ''}
                        {it.kind === 'panel' ? `SKU ${it.sku}` : `${it.markers?.length || 0} markers`}
                      </div>
                    </div>
                    <div className="tabular-nums text-ink-900">${(it.price * (it.qty || 1)).toFixed(0)}</div>
                  </li>
                ))}
              </ul>

              <dl className="mt-5 space-y-2.5 text-[14px]">
                <div className="flex justify-between text-ink-700">
                  <dt>Subtotal</dt>
                  <dd className="tabular-nums">${subtotal.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between text-ink-700">
                  <dt>Lab processing</dt>
                  <dd className="tabular-nums">${fee.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between text-ink-700">
                  <dt>Tax</dt>
                  <dd className="tabular-nums">${tax.toFixed(2)}</dd>
                </div>
              </dl>

              <div className="mt-4 pt-4 border-t border-cream-200 flex items-end justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">Total</div>
                  <div className="text-[12px] text-ink-500">USD</div>
                </div>
                <div className="font-display text-3xl text-ink-900">
                  <span className="text-lg align-top">$</span>{total.toFixed(2)}
                </div>
              </div>

              <div className="mt-6 p-4 rounded-2xl bg-cream-100 border border-cream-200 text-[12px] text-ink-600 flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 text-brick-500 mt-0.5" />
                Encrypted in transit. Need help? {CONTACT_INFO.email}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
};

const ReviewBlock = ({ title, onEdit, children }) => (
  <div className="p-5 rounded-2xl bg-cream-100 border border-cream-200">
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{title}</div>
      <button onClick={onEdit} className="text-[12px] text-brick-500 hover:underline">
        Edit
      </button>
    </div>
    <div className="mt-2 text-[14px] text-ink-900 space-y-0.5">{children}</div>
  </div>
);

export default CheckoutPage;
